import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarMonth } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Alert, Button, DialogActions, DialogContent, Divider, InputAdornment } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import { enqueueSnackbar } from "notistack";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TbBrandDaysCounter, TbHistory } from "react-icons/tb";
import { z } from "zod";

import { CommonDatePicker, CommonInput, CommonLabel } from "@/components/CommonInput";
import { useUpdateUserLifeCycleMutation } from "@/requests/user.mutations";
import { useUsersTableActions, useUsersUpdateLifeCycleDialog } from "@/stores/use-users-table";
import { User, UserLifeCycle, UserType } from "@/types/user.types";

import { UserLifecycleInfoTimelineItem } from "../userLifecycleDialog/UserLifecycleTimeLineItem";

const formSchema = z
    .object({
        dateRange: z.object({
            start: z.custom<DateTime | null>((val) => val !== null && val instanceof DateTime, {
                message: "Lütfen geçerli bir giriş tarihi giriniz.",
            }),
            end: z.custom<DateTime | null>(),
        }),
        isDeleted: z.boolean(),
        userType: z.nativeEnum(UserType),
    })
    .superRefine(({ dateRange, isDeleted, userType }, ctx) => {
        const { start, end } = dateRange;
        if (isDeleted && !end) {
            ctx.addIssue({
                path: ["dateRange", "end"],
                message: "Silinmiş kullanıcı için çıkış tarihi girilmesi zorunludur.",
                code: z.ZodIssueCode.custom,
            });
        }
        if (end && userType === UserType.ADMIN) {
            ctx.addIssue({
                path: ["dateRange", "end"],
                message: "Admin için çıkış tarihi girilemez.",
                code: z.ZodIssueCode.custom,
            });
        }
        if (end && start) {
            if (end <= start) {
                ctx.addIssue({
                    path: ["dateRange"],
                    message: "Çıkış tarihi,giriş tarihinden küçük veya eşit olamaz.",
                    code: z.ZodIssueCode.custom,
                });
            }
        }
    });

export type UpdateLifeCycleType = z.infer<typeof formSchema>;

function UserLifecyclesPreview({ lifecycles }: { lifecycles: UserLifeCycle[] }) {
    const pastLifeCycles = (lifecycles || []).filter((lifecycle) => lifecycle.leavedAt);
    if (!pastLifeCycles.length) return null;
    return (
        <>
            <div className="p-4  z-10 relative">
                <div className="font-medium flex items-center gap-2 mb-2">
                    <TbHistory />
                    Kullanıcının Çalışma Geçmişi
                </div>
                <div className="flex flex-col gap-3 ">
                    {pastLifeCycles.map((item, index) => {
                        return <UserLifecycleInfoTimelineItem key={item.id} lifecycle={item} index={index} />;
                    })}
                </div>
            </div>
            <Divider />
        </>
    );
}

function UpdateLifeCycleDialog() {
    const { t, i18n } = useTranslation();
    const { updateShowUpdateUserLifeCycleDialog } = useUsersTableActions();
    const user = useUsersUpdateLifeCycleDialog() as User;
    const { isPending: loading, mutateAsync } = useUpdateUserLifeCycleMutation();
    const activeLifeCycle = (user?.lifecycles || []).at(-1);

    const startedAt = activeLifeCycle?.startedAt ? DateTime.fromISO(activeLifeCycle.startedAt) : null;
    const leavedAt = activeLifeCycle?.leavedAt ? DateTime.fromISO(activeLifeCycle.leavedAt) : null;
    const {
        handleSubmit,
        formState: { errors },
        control,
        watch,
    } = useForm({
        defaultValues: {
            isDeleted: !!user?.isDeleted,
            dateRange: {
                start: startedAt,
                end: leavedAt,
            },
            userType: user?.userType || UserType.DRIVER,
        },
        disabled: loading,
        resolver: zodResolver(formSchema),
    });
    const dateRange = watch("dateRange");

    const workingDuration = useMemo(() => {
        const { start, end } =
            dateRange || ({ start: null, end: null } as unknown as { start: DateTime | null; end: DateTime | null });

        if (!start || !end) return "";

        const diff = end.diff(start, ["months", "days"]);

        return `${Math.floor(diff.months)} ay ${Math.round(diff.days)} gün`;
    }, [dateRange]);

    const createUser = async ({ dateRange }: UpdateLifeCycleType) => {
        if (loading || !user.id || !activeLifeCycle) return;
        const { start, end } = dateRange;
        if (!start) {
            enqueueSnackbar("Giriş tarihi zorunludur.", {
                variant: "error",
            });
            return;
        }

        try {
            const res = await mutateAsync({
                leavedAt: end,
                startedAt: start,
                id: user.id,
                lifecycleId: activeLifeCycle.id,
            });
            const message = res.message || "";
            if (res.ok) {
                handleClose();
                enqueueSnackbar(message, {
                    variant: "success",
                });
            } else {
                enqueueSnackbar(message, {
                    variant: "error",
                });
            }
        } catch (error) {
            console.error("User error:", error);
        }
    };
    const handleClose = () => {
        updateShowUpdateUserLifeCycleDialog();
    };
    if (!user) return null;

    return (
        <>
            <DialogContent dividers>
                <div className="flex flex-col gap-6">
                    <UserLifecyclesPreview lifecycles={user?.lifecycles} />

                    <div className=" px-4">
                        <div className="font-medium flex items-center gap-2 mb-2">
                            <TbHistory />
                            Güncel & Son Çalışma Durumu
                        </div>
                        <Alert severity="info" sx={{ margin: "0px !important", padding: "0px !important" }}>
                            Kullanıcı için <span className="font-semibold">Giriş-Çıkış Tarihleri</span> seçiniz.
                            <span className="font-semibold">Çıkış Tarihi</span> seçildiyse, kullanıcı otomatik olarak
                            silinecektir.
                        </Alert>
                        <form autoComplete="off" onSubmit={handleSubmit(createUser)} className="grid grid-cols-4 gap-7">
                            <div className=" col-span-2"></div>
                            <CommonLabel
                                description={t("automaticCalculates")}
                                label={t("workingPeriod")}
                                className=" col-span-2"
                            >
                                <CommonInput
                                    value={workingDuration}
                                    placeholder="X Ay Y Gün"
                                    disabled
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <TbBrandDaysCounter size={24} />
                                        </InputAdornment>
                                    }
                                ></CommonInput>
                            </CommonLabel>
                            <CommonLabel label={t("userLifeCycle")} className="col-span-4">
                                <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={i18n.language}>
                                    <Controller
                                        name="dateRange"
                                        control={control}
                                        render={({ field }) => {
                                            return (
                                                <CommonDatePicker
                                                    {...field}
                                                    value={[field.value?.start, field.value?.end]}
                                                    onChange={([start, end]) => {
                                                        field.onChange({ start, end });
                                                    }}
                                                    localeText={{ start: "", end: "" }}
                                                    ampm={false}
                                                    disabled={loading}
                                                    slotProps={{
                                                        textField: (x) => {
                                                            const isEnd = x?.position === "end";
                                                            const dateRangeErr = errors.dateRange;

                                                            return {
                                                                variant: "filled",
                                                                error:
                                                                    !!dateRangeErr &&
                                                                    (isEnd
                                                                        ? !!dateRangeErr.end?.message
                                                                        : !dateRangeErr.start?.message),
                                                                helperText:
                                                                    !!dateRangeErr &&
                                                                    (isEnd
                                                                        ? dateRangeErr.end?.message
                                                                        : dateRangeErr.start?.message),
                                                                InputProps: {
                                                                    startAdornment: (
                                                                        <InputAdornment
                                                                            className=" -translate-y-2"
                                                                            position="start"
                                                                        >
                                                                            <CalendarMonth fontSize="small" />
                                                                        </InputAdornment>
                                                                    ),
                                                                },
                                                            };
                                                        },
                                                    }}
                                                />
                                            );
                                        }}
                                    />
                                </LocalizationProvider>
                            </CommonLabel>
                        </form>
                    </div>
                </div>
            </DialogContent>
            <DialogActions>
                <Button disabled={loading} autoFocus color="secondary" variant="outlined" onClick={handleClose}>
                    {t("buttons.cancel")}
                </Button>

                <LoadingButton
                    loading={loading}
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit(createUser)}
                >
                    {t("buttons.update")}
                </LoadingButton>
            </DialogActions>
        </>
    );
}

export default UpdateLifeCycleDialog;
